// ============================================================
// RhythmInputPanel
// ============================================================

import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import { v4 as uuid } from 'uuid';
import type { RhythmSlot, NoteValue, TimeSignature } from '../../core/types';
import { noteValueBeats, noteValueLabel, noteValueKey } from '../../core/types';

const ROW1: NoteValue[] = ['whole', 'dottedHalf', 'half', 'dottedQuarter', 'quarter'];
const ROW2: NoteValue[] = ['tripletQuarter', 'dottedEighth', 'eighth', 'tripletEighth', 'sixteenth'];
const ALL_VALUES: NoteValue[] = [...ROW1, ...ROW2];
const TRIPLET_VALUES = new Set<NoteValue>(['tripletQuarter', 'tripletEighth']);

// ------------------------------------------------------------
// SVG NOTE ICONS — exported so SlashBar can reuse them
// ------------------------------------------------------------

export function NoteIcon({ nv, size = 28 }: { nv: NoteValue; size?: number }) {
  const s = size;
  const cx = s * 0.42;
  const cy = s * 0.72;
  const rx = s * 0.22;
  const ry = s * 0.16;
  const stemX = cx + rx * 0.85;
  const stemTop = s * 0.18;
  const sw = s * 0.07;
  const dotX = cx + rx + s * 0.12;
  const dotY = cy - ry * 0.3;
  const dotR = s * 0.07;

  const head = (filled: boolean) => (
    <ellipse
      cx={cx} cy={cy} rx={rx} ry={ry}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor" strokeWidth={sw}
      transform={`rotate(-12 ${cx} ${cy})`}
    />
  );
  const stem  = <line x1={stemX} y1={cy - ry * 0.5} x2={stemX} y2={stemTop} stroke="currentColor" strokeWidth={sw} strokeLinecap="round" />;
  const dot   = <circle cx={dotX} cy={dotY} r={dotR} fill="currentColor" />;
  const flag1 = <path d={`M${stemX} ${stemTop} Q${stemX + s*0.22} ${stemTop + s*0.1} ${stemX + s*0.18} ${stemTop + s*0.22}`} fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" />;
  const flag2 = <path d={`M${stemX} ${stemTop + s*0.14} Q${stemX + s*0.22} ${stemTop + s*0.24} ${stemX + s*0.18} ${stemTop + s*0.36}`} fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" />;
  const three = <text x={stemX + s*0.1} y={stemTop - s*0.02} fontSize={s*0.19} fill="currentColor" fontFamily="serif" textAnchor="middle">3</text>;

  switch (nv) {
    case 'whole':          return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>{head(false)}</svg>;
    case 'half':           return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>{head(false)}{stem}</svg>;
    case 'dottedHalf':     return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>{head(false)}{stem}{dot}</svg>;
    case 'quarter':        return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>{head(true)}{stem}</svg>;
    case 'dottedQuarter':  return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>{head(true)}{stem}{dot}</svg>;
    case 'eighth':         return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>{head(true)}{stem}{flag1}</svg>;
    case 'dottedEighth':   return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>{head(true)}{stem}{flag1}{dot}</svg>;
    case 'sixteenth':      return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>{head(true)}{stem}{flag1}{flag2}</svg>;
    case 'tripletQuarter': return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>{head(true)}{stem}{three}</svg>;
    case 'tripletEighth':  return <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>{head(true)}{stem}{flag1}{three}</svg>;
  }
}

// ------------------------------------------------------------
// NOTE BUTTON
// ------------------------------------------------------------

function NoteButton({ nv, onClick, disabled }: {
  nv: NoteValue; onClick: () => void; disabled: boolean;
}) {
  const isTriplet = TRIPLET_VALUES.has(nv);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={`${noteValueLabel(nv)} [${noteValueKey(nv)}]`}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'flex-end', gap: 3, padding: '8px 4px 6px',
        background: isTriplet
          ? 'color-mix(in srgb, var(--color-accent) 8%, var(--color-bg))'
          : 'var(--color-bg)',
        border: `1px solid ${isTriplet ? 'var(--color-accent)' : 'var(--color-rule)'}`,
        borderRadius: 7, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.28 : 1, flex: 1, minWidth: 0,
        transition: 'opacity 0.1s, transform 0.07s',
        color: 'var(--color-ink-primary)',
      }}
      onMouseDown={(e) => { if (!disabled) (e.currentTarget as HTMLElement).style.transform = 'scale(0.91)'; }}
      onMouseUp={(e)    => { (e.currentTarget as HTMLElement).style.transform = ''; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = ''; }}
    >
      <NoteIcon nv={nv} size={26} />
      <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--color-ink-muted)', lineHeight: 1 }}>
        [{noteValueKey(nv)}]
      </span>
    </button>
  );
}

// ------------------------------------------------------------
// SLOT STRIP
// ------------------------------------------------------------

function SlotStrip({ slots, totalBeats, filledBeats, onDelete }: {
  slots: RhythmSlot[]; totalBeats: number; filledBeats: number; onDelete: (id: string) => void;
}) {
  const overflow = filledBeats > totalBeats + 0.001;
  return (
    <div style={{ display: 'flex', gap: 2, height: 48, marginBottom: 6 }}>
      {slots.map((slot) => {
        const wPct = (slot.beats / totalBeats) * 100;
        const isTriplet = TRIPLET_VALUES.has(slot.noteValue);
        return (
          <div
            key={slot.id}
            onClick={() => onDelete(slot.id)}
            title={`${noteValueLabel(slot.noteValue)} — click to remove`}
            style={{
              flexBasis: `${wPct}%`, flexShrink: 0, minWidth: 18,
              background: overflow
                ? 'color-mix(in srgb, var(--color-error) 12%, transparent)'
                : isTriplet
                ? 'color-mix(in srgb, var(--color-accent) 10%, transparent)'
                : 'var(--color-hover)',
              border: `1px solid ${overflow ? 'var(--color-error)' : isTriplet ? 'var(--color-accent)' : 'var(--color-rule)'}`,
              borderRadius: 4, display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer',
              color: overflow ? 'var(--color-error)' : 'var(--color-ink-primary)',
            }}
          >
            <NoteIcon nv={slot.noteValue} size={20} />
          </div>
        );
      })}
      {filledBeats < totalBeats - 0.001 && (
        <div style={{
          flexBasis: `${((totalBeats - filledBeats) / totalBeats) * 100}%`,
          flexShrink: 0, minWidth: 4,
          border: '1px dashed var(--color-rule)', borderRadius: 4, opacity: 0.3,
        }} />
      )}
    </div>
  );
}

// ------------------------------------------------------------
// PROGRESS
// ------------------------------------------------------------

function Progress({ filled, total }: { filled: number; total: number }) {
  const pct   = Math.min(1, filled / total);
  const exact = Math.abs(filled - total) < 0.001;
  const over  = filled > total + 0.001;
  const color = over ? 'var(--color-error)' : exact ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-accent) 55%, var(--color-rule))';
  const fmt   = (n: number) => n % 1 === 0 ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ height: 3, background: 'var(--color-rule)', borderRadius: 2, overflow: 'hidden', marginBottom: 5 }}>
        <div style={{ height: '100%', width: `${pct * 100}%`, background: color, borderRadius: 2, transition: 'width 0.12s, background 0.12s' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--color-ink-muted)' }}>
          {over ? 'Too many beats — remove a slot' : exact ? 'Click a slot to remove it' : `${fmt(total - filled)} beat${total - filled === 1 ? '' : 's'} remaining`}
        </span>
        <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 600, color }}>
          {exact ? `${total}/${total} ✓` : over ? `${fmt(filled)}/${total} ✗` : `${fmt(filled)} / ${total}`}
        </span>
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Internal helper (not exported — use rhythmUtils.ts instead)
// ------------------------------------------------------------

export function slotsToPositions(slots: RhythmSlot[]): number[] {
  return slots.map((s) => s.position);
}

// ------------------------------------------------------------
// MAIN COMPONENT
// ------------------------------------------------------------

interface RhythmInputPanelProps {
  timeSignature: TimeSignature;
  initialSlots?: RhythmSlot[];
  onConfirm: (slots: RhythmSlot[]) => void;
  onClose: () => void;
}

export function RhythmInputPanel({ timeSignature, initialSlots = [], onConfirm, onClose }: RhythmInputPanelProps) {
  const [slots, setSlots]  = useState<RhythmSlot[]>(initialSlots);
  const panelRef           = useRef<HTMLDivElement>(null);
  const totalBeats         = timeSignature.numerator;
  const filledBeats        = useMemo(() => slots.reduce((s, sl) => s + sl.beats, 0), [slots]);
  const isExact            = Math.abs(filledBeats - totalBeats) < 0.001 || slots.length === 0;

  const slotsWithPositions = useMemo<RhythmSlot[]>(() => {
    let pos = 0;
    return slots.map((s) => { const r = { ...s, position: pos }; pos += s.beats; return r; });
  }, [slots]);

  const addSlot       = useCallback((nv: NoteValue) => {
    const beats = noteValueBeats(nv);
    if (beats > totalBeats - filledBeats + 0.001) return;
    setSlots((p) => [...p, { id: uuid(), noteValue: nv, beats, position: 0 }]);
  }, [filledBeats, totalBeats]);

  const deleteSlot    = useCallback((id: string) => setSlots((p) => p.filter((s) => s.id !== id)), []);
  const deleteLast    = useCallback(() => setSlots((p) => p.slice(0, -1)), []);
  const clearAll      = useCallback(() => setSlots([]), []);
  const handleConfirm = useCallback(() => { if (isExact) onConfirm(slotsWithPositions); }, [isExact, slotsWithPositions, onConfirm]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'Escape')    { onClose(); return; }
      if (e.key === 'Enter')     { handleConfirm(); return; }
      if (e.key === 'Backspace') { e.preventDefault(); deleteLast(); return; }
      const nv = ALL_VALUES.find((v) => noteValueKey(v) === e.key);
      if (nv) { e.preventDefault(); addSlot(nv); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [addSlot, deleteLast, handleConfirm, onClose]);

  useEffect(() => { panelRef.current?.focus(); }, []);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        style={{
          background: 'var(--color-paper)',
          border: '1px solid var(--color-rule)',
          borderRadius: 12,
          padding: '18px 18px 16px',
          width: 420,
          maxWidth: 'calc(100vw - 24px)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
          outline: 'none',
          isolation: 'isolate',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-ink-primary)', fontFamily: 'var(--font-sans)', letterSpacing: '-0.02em' }}>
              Rhythm Input
            </div>
            <div style={{ fontSize: 10, color: 'var(--color-accent)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
              {timeSignature.numerator}/{timeSignature.denominator} · click or press keys · Backspace removes last
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--color-rule)', borderRadius: 5, padding: '3px 9px', cursor: 'pointer', fontSize: 11, color: 'var(--color-ink-muted)', lineHeight: 1.2 }}>✕</button>
        </div>

        {/* Row 1 */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
          {ROW1.map((nv) => (
            <NoteButton key={nv} nv={nv} onClick={() => addSlot(nv)} disabled={noteValueBeats(nv) > totalBeats - filledBeats + 0.001} />
          ))}
        </div>

        {/* Row 2 */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
          {ROW2.map((nv) => (
            <NoteButton key={nv} nv={nv} onClick={() => addSlot(nv)} disabled={noteValueBeats(nv) > totalBeats - filledBeats + 0.001} />
          ))}
        </div>

        <Progress filled={filledBeats} total={totalBeats} />

        {slots.length > 0 ? (
          <SlotStrip slots={slotsWithPositions} totalBeats={totalBeats} filledBeats={filledBeats} onDelete={deleteSlot} />
        ) : (
          <div style={{ height: 48, border: '1px dashed var(--color-rule)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-ink-muted)', fontSize: 11, fontFamily: 'var(--font-mono)', marginBottom: 6, opacity: 0.5 }}>
            Add note values above
          </div>
        )}

        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 10 }}>
          <button onClick={clearAll} disabled={slots.length === 0} style={{ background: 'transparent', border: '1px solid var(--color-rule)', borderRadius: 6, padding: '6px 12px', cursor: slots.length === 0 ? 'not-allowed' : 'pointer', fontSize: 12, color: 'var(--color-ink-muted)', opacity: slots.length === 0 ? 0.4 : 1 }}>Clear</button>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--color-rule)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, color: 'var(--color-ink-muted)' }}>Cancel</button>
          <button onClick={handleConfirm} disabled={!isExact} style={{ background: isExact ? 'var(--color-accent)' : 'var(--color-rule)', border: 'none', borderRadius: 6, padding: '6px 16px', cursor: isExact ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 700, color: isExact ? 'white' : 'var(--color-ink-muted)', transition: 'background 0.15s' }}>
            {slots.length === 0 ? 'Clear rhythm ↵' : 'Confirm ↵'}
          </button>
        </div>
      </div>
    </div>
  );
}